import { useMemo } from "react";
import { useConversations } from "@/hooks/conversation/useConversations";
import { aggregateKnowledgeGaps } from "@/lib/knowledge/aggregateGaps";
import type { Agent } from "@/types/agent/agent";
import type { KnowledgeGapsResult } from "@/types/knowledge/knowledge-brain";

/** Window scanned for failed questions. Matches the conversation log default. */
const TIME_RANGE = "30d";

const EMPTY: KnowledgeGapsResult = {
  failedQuestions: [],
  askedQuestions: [],
  confidentTopics: [],
  thinTopics: [],
  conversationsScanned: 0,
};

/**
 * Sentinel agent id used when the caller explicitly asked to scope to one or
 * more agents but none of them resolved a real id yet (e.g. a per-Genie page
 * calling this before its Genie has loaded). It can never match a real
 * `agent_id` column value, so the query comes back empty.
 *
 * This exists to defend against `supabase/functions/api/handlers/conversations/list.ts`:
 * that handler treats an *empty* `agentIds` array as "no agents specified" and
 * silently falls back to every agent the authenticated user owns. That
 * fallback is the correct, intentional behaviour for a genuine workspace-wide
 * caller (`useKnowledgeGaps()` / `useKnowledgeGaps([])`), but it would be a
 * real scope leak for a caller that passed specific agents whose ids just
 * hadn't resolved — it would silently widen a "this one Genie" gap scan into
 * "this whole workspace" instead of failing loudly or scanning nothing.
 */
const UNRESOLVABLE_AGENT_IDS: string[] = ["__no-agent-id-resolved__"];

/**
 * Cross-conversation knowledge gaps for the Knowledge Base page.
 *
 * Composes the existing `useConversations` infinite query over the last 30 days
 * and runs the shared hedge detector across every loaded page. Only the first
 * page loads by default (30 conversations) — the panel exposes "scan more" via
 * `fetchNextPage` rather than silently pulling the whole history.
 *
 * Scoping: pass `[genie]` (or any `Partial<Agent>[]`) to scope gap detection
 * to those agents' conversations only; pass nothing (or `[]`) for the
 * workspace-wide view across every agent the user owns. See
 * `UNRESOLVABLE_AGENT_IDS` above for why an explicit-but-unresolved agent list
 * does not fall through to the workspace-wide behaviour.
 */
export const useKnowledgeGaps = (agents: Partial<Agent>[] = []) => {
  const agentIds = useMemo(() => {
    const resolved = agents
      .map((agent) => agent.id)
      .filter((id): id is string => Boolean(id));
    if (agents.length > 0 && resolved.length === 0) {
      return UNRESOLVABLE_AGENT_IDS;
    }
    return resolved;
  }, [agents]);

  const agentNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const agent of agents) {
      if (agent.id && agent.name) map[agent.id] = agent.name;
    }
    return map;
  }, [agents]);

  const query = useConversations(agentIds, TIME_RANGE);

  const gaps = useMemo(() => {
    const conversations = (query.data?.pages ?? []).flatMap((page) => page.data);
    if (conversations.length === 0) return EMPTY;
    return aggregateKnowledgeGaps(conversations, { agentNames });
  }, [query.data, agentNames]);

  return {
    gaps,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: Boolean(query.hasNextPage),
    isScanningMore: query.isFetchingNextPage,
    scanMore: query.fetchNextPage,
  };
};
